import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Search, Plus, Trash2, X, Filter, FileText, FileSpreadsheet,
  RefreshCcw, ChevronDown, ChevronUp, Upload, Download,
  FileOutput, LayoutGrid, AlertTriangle, CheckCircle,
  TrendingUp, Hash, Tag, DollarSign, Layers, MapPin, Truck
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, exactColumnRule, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import localApiAdapter from '../../core/services/localApiAdapter';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import {sliceQawaemStats, QAWAEM_KPI_GRID_CLASS, QAWAEM_TABLE_ACTIONS_TH_CLASS, QawaemTableSelectAllHeader, QawaemTableSelectRowCell, createArticleStatusChangeHandler, mapStatusOptionsToColumnFilter, useQawaemColumnVisibility, QawaemTableStickyActionsTd } from './articlesListPageUtils';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { useModuleConfigTheme } from '../../shared/hooks/useModuleConfigTheme';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useArticlesConfig } from './useArticlesConfig';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import {
  buildCategorySelectOptions,
  CATALOG_CATEGORIES_REFRESH_EVENT,
  fetchCatalogCategories,
  findCategoryByRef,
  resolveCategoryLabel,
} from './articleCatalogCategories';
// Tables-mobile.css - DELETED (not needed)

const PartsPage = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  useArticlesConfig();
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const p = (key, opts) => t(`modules.article.pages.partsPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const [searchTerm, setSearchTerm] = useState('');
  const [parts, setParts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [viewingPart, setViewingPart] = useState(null);
  const [selectedParts, setSelectedParts] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Form data with collapsible sections
  const [formData, setFormData] = useState({
    part_number: '', name: '', description: '', category: '', unit_price: '',
    quantity_in_stock: '', min_stock_level: '', location: '', supplier: '', status: ''
  });

  const [formTab, setFormTab] = useState('info');

  // Advanced Options states
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_parts');
  const [quickFilter, setQuickFilter] = useState('all');

  // Sorting state
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();
  const entityLabel = p('entity_label', { defaultValue: 'قطعة الغيار' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'قطعة غيار' });

  // Dropdown states and refs
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    loadParts();
    loadSuppliers();
    void loadCategories();
  }, [refreshTrigger]);

  useEffect(() => {
    const handleCategoriesRefresh = () => { void loadCategories(); };
    window.addEventListener(CATALOG_CATEGORIES_REFRESH_EVENT, handleCategoriesRefresh);
    return () => window.removeEventListener(CATALOG_CATEGORIES_REFRESH_EVENT, handleCategoriesRefresh);
  }, []);

  useEffect(() => {
    if (!showModal) return;
    void loadCategories();
  }, [showModal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadParts = async () => {
    try {
      const response = await localApiAdapter.get('/api/articles/spare-parts');
      const data = response.data || response;
      setParts(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading parts:', error);
      setParts([]);
    }
  };

  const loadSuppliers = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/fournisseurs');
      const data = response?.data ?? response;
      setSuppliers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setSuppliers([]);
    }
  };

  const loadCategories = async () => {
    setCategories(await fetchCatalogCategories('product'));
  };

  const computeNextPartNumber = useCallback(() => {
    const prefix = getEntityPrefix(settings, 'sparePart', 'PART');
    return generateEntityReference(prefix, parts, (part) => part.part_number);
  }, [parts, settings]);

  const categorySelectOptions = useMemo(
    () => buildCategorySelectOptions(categories, t),
    [categories, t],
  );

  const getCategoryName = useCallback(
    (categoryRef) => resolveCategoryLabel(categoryRef, categories, { emptyLabel: p('na') }),
    [categories, p],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert(p('required_fields'));
      return;
    }

    try {
      const partNumber = (formData.part_number || '').trim() || computeNextPartNumber();
      const submitData = {
        ...formData,
        part_number: partNumber,
        unit_price: parseFloat(formData.unit_price) || 0,
        quantity_in_stock: parseInt(formData.quantity_in_stock) || 0,
        min_stock_level: parseInt(formData.min_stock_level) || 0
      };
      if (editingPart) {
        await localApiAdapter.put(`/api/articles/spare-parts/${editingPart.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/api/articles/spare-parts', submitData);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error saving part:', error);
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleEdit = (part) => {
    setEditingPart(part);
    setFormData({
      part_number: part.part_number,
      name: part.name,
      description: part.description || '',
      category: findCategoryByRef(categories, part.category)?.id || part.category || '',
      unit_price: part.unit_price?.toString() || '',
      quantity_in_stock: part.quantity_in_stock?.toString() || '',
      min_stock_level: part.min_stock_level?.toString() || '',
      location: part.location || '',
      supplier: part.supplier || '',
      status: part.status
    });
    setFormTab('info');
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingPart(null);
    setFormData({
      part_number: computeNextPartNumber(),
      name: '', description: '', category: '', unit_price: '',
      quantity_in_stock: '', min_stock_level: '', location: '', supplier: '', status: ''
    });
    setFormTab('info');
    setShowModal(true);
  };

  

  const handleDelete = (id) => openDeleteOne(id);

  const handleBulkDelete = () => {
    if (!selectedParts.length) return;
    openBulkDelete(selectedParts);
  };

  const executeDeleteConfirmed = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        await localApiAdapter.delete(`/api/articles/spare-parts/${ids[0]}`);
        notifyDeleted(showNotify, entityLabel);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        await Promise.all(ids.map((id) => localApiAdapter.delete(`/api/articles/spare-parts/${id}`)));
        notifyBulkDeleted(showNotify, ids.length, entityLabelPlural);
        setSelectedParts([]);
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error deleting part:', error);
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleView = (part) => {
    setViewingPart(part);
    setShowViewModal(true);
  };

  const STATUS_OPTIONS = useMemo(
    () => [
      { value: 'available', label: p('status_available'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
      { value: 'low_stock', label: p('status_low_stock'), icon: AlertTriangle, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      { value: 'out_of_stock', label: p('status_out_of_stock'), icon: X, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    ],
    [t],
  );

  const statusColumnFilterOptions = useMemo(
    () => mapStatusOptionsToColumnFilter(STATUS_OPTIONS),
    [STATUS_OPTIONS],
  );

  const handleStatusChange = useCallback(
    createArticleStatusChangeHandler('/api/articles/spare-parts', () => setRefreshTrigger((prev) => prev + 1)),
    [],
  );

  const resetForm = () => {
    setFormData({
      part_number: '', name: '', description: '', category: '', unit_price: '',
      quantity_in_stock: '', min_stock_level: '', location: '', supplier: '', status: ''
    });
    setEditingPart(null);
    setFormTab('info');
    setShowModal(false);
  };

  const formTabs = useMemo(() => ([
    { id: 'info', label: p('section_info'), icon: Package },
    { id: 'stock', label: p('section_stock_location'), icon: Layers },
  ]), [p]);

  const handleSelectPart = (id) => {
    setSelectedParts(selectedParts.includes(id) ? selectedParts.filter(pId => pId !== id) : [...selectedParts, id]);
  };

  // Calculate stats
  const partsArray = Array.isArray(parts) ? parts : [];
  const stats = {
    total: partsArray.length,
    available: partsArray.filter(p => p.status === 'available').length,
    lowStock: partsArray.filter(p => p.status === 'low_stock').length,
    outOfStock: partsArray.filter(p => p.status === 'out_of_stock').length,
    totalValue: partsArray.reduce((sum, p) => sum + (p.unit_price * p.quantity_in_stock), 0)
  };

  // Main view tabs
  const mainTabs = [
    { id: 'all_parts', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_categorie', name: p('tab_by_category'), icon: Tag },
    { id: 'par_statut', name: p('tab_by_status'), icon: TrendingUp }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Package },
    { id: 'available', name: p('filter_available'), icon: CheckCircle },
    { id: 'low_stock', name: p('filter_low_stock'), icon: AlertTriangle },
    { id: 'out_of_stock', name: p('filter_out_of_stock'), icon: X }
  ];

  const partsDistribution = {
    all_parts: {
      title: p('overview_title'),
      data: [
        { name: p('status_available'), count: stats.available, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { name: p('status_low_stock'), count: stats.lowStock, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { name: p('status_out_of_stock'), count: stats.outOfStock, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    par_categorie: {
      title: p('distribution_by_category'),
      data: Array.from(new Set(parts.map(p => p.category).filter(Boolean))).slice(0, 3).map(cat => ({
        name: cat,
        count: parts.filter(p => p.category === cat).length,
        color: "bg-gradient-to-r from-blue-500 to-blue-600"
      }))
    },
    par_statut: {
      title: p('distribution_by_status'),
      data: [
        { name: p('status_available'), count: stats.available, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { name: p('status_low_stock'), count: stats.lowStock, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { name: p('status_out_of_stock'), count: stats.outOfStock, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    }
  };

  const distributionInfo = partsDistribution[mainViewMode] || partsDistribution.all_parts;

  const baseFilteredParts = useMemo(
    () =>
      partsArray.filter((part) => {
        const matchesSearch =
          !searchTerm ||
          part.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          part.part_number?.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;
        if (quickFilter === 'available' && part.status !== 'available') return false;
        if (quickFilter === 'low_stock' && part.status !== 'low_stock') return false;
        if (quickFilter === 'out_of_stock' && part.status !== 'out_of_stock') return false;
        return true;
      }),
    [partsArray, searchTerm, quickFilter],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('reference', (r) => r.part_number || ''),
      textColumnRule('name', (r) => r.name || ''),
      textColumnRule('price', (r) => (r.unit_price ? formatBrandMoney(r.unit_price) : p('na'))),
      textColumnRule('stock', (r) => String(r.quantity_in_stock ?? '')),
      textColumnRule('category', (r) => getCategoryName(r.category)),
      textColumnRule('location', (r) => r.location || ''),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [t],
  );

  const filteredParts = useMemo(
    () => applyColumnFilters(baseFilteredParts, colFilters, columnFilterRules),
    [baseFilteredParts, colFilters, columnFilterRules],
  );

  const filteredPartIds = useMemo(() => filteredParts.map((p) => p.id), [filteredParts]);
  const allRowsSelected = filteredPartIds.length > 0 && filteredPartIds.every((id) => selectedParts.includes(id));
  const someRowsSelected = selectedParts.length > 0 && !allRowsSelected;
  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) setSelectedParts([]);
    else setSelectedParts(filteredPartIds);
  };

  const getStatusBadge = (status) => {
    const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>
        <Icon size={14} />
        {label}
      </span>
    );
  };

  const { columnVisibility, handleToggleColumn, showCol, visibleDataColCount } = useQawaemColumnVisibility([
    'reference', 'name', 'category', 'price', 'stock', 'location', 'status',
  ]);

  const qawaemColumns = useMemo(
    () => [
      { key: 'reference', label: p('col_reference'), checked: columnVisibility.reference !== false, required: true },
      { key: 'name', label: p('col_name'), checked: columnVisibility.name !== false, required: true },
      { key: 'category', label: p('col_category'), checked: columnVisibility.category !== false },
      { key: 'price', label: p('col_price'), checked: columnVisibility.price !== false },
      { key: 'stock', label: p('col_stock'), checked: columnVisibility.stock !== false },
      { key: 'location', label: p('col_location'), checked: columnVisibility.location !== false },
      { key: 'status', label: t('common.status'), checked: columnVisibility.status !== false },
      { key: 'actions', label: t('common.actions'), checked: true, required: true },
    ],
    [t, p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked).length;
  const tableColSpan = 1 + visibleDataColCount + 1;

  return (
    <div className={`space-y-6 employees-container`}>
      {/* Header - BLUE THEME */}
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700">
              <Package className="h-8 w-8 text-white" />
            </div>
            {p('title')}
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${
              showAdvancedOptions
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30'
                : 'bg-white/5 border-white/20'
            } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            title={p('advanced_options')}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          <button
            onClick={openCreateModal}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-blue-500/30"
          >
            <Plus className="h-5 w-5" />
            <span>{p('add_button')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={QAWAEM_KPI_GRID_CLASS}>
        {sliceQawaemStats([
          { title: p('stat_total'), value: stats.total, icon: Package, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_available'), value: stats.available, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_low_stock'), value: stats.lowStock, icon: AlertTriangle, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_out_of_stock'), value: stats.outOfStock, icon: X, color: 'from-red-500 to-red-600' },
          { title: p('stat_stock_value'), value: `${(stats.totalValue / 1000).toFixed(0)}K`, icon: TrendingUp, color: 'from-purple-500 to-purple-600' }
        ], 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options - Collapsible */}
      <motion.div
        initial={false}
        animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{ overflow: "hidden" }}
      >
        <div className="space-y-6 pt-6">
          {/* Main Tabs */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); }}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    mainViewMode === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Filters */}
          {mainViewMode === 'all_parts' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setQuickFilter(filter.id)}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      quickFilter === filter.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Distribution Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <h3 className="text-xl font-semibold text-white mb-4">{distributionInfo.title}</h3>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-white">{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full">
                    <div
                      className={`h-3 rounded-full ${item.color}`}
                      style={{ width: `${stats.total > 0 ? (item.count / stats.total * 100) : 0}%` }}
                    ></div>
                  </div>
                  <p className="mt-2 text-sm text-white/60">
                    {stats.total > 0 ? (item.count / stats.total * 100).toFixed(1) : 0}{p('percent_of_total')}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Table Section */}
      <div className="glass-panel glass-table-shell overflow-visible">
          {/* Bulk Actions */}
          {selectedParts.length > 0 && (
            <div className="flex items-center gap-2 bg-white/10 py-2 px-4 rounded-xl mt-4">
              <span className="text-white font-medium">{p('selected_count', { count: selectedParts.length })}</span>
              <button
                onClick={handleBulkDelete}
                className="bg-red-500/20 text-red-400 p-2 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-1"
              >
                <Trash2 size={18} />
                <span>{t('common.delete')}</span>
              </button>
            </div>
          )}

        <div className="overflow-x-auto table-horizontal-scroll">
          <table className="w-full employees-table data-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} />
                {showCol('reference') ? (
                  <TableColumnHeaderFilter columnKey="reference" label={p('col_reference')} value={colFilters.reference ?? ''} onChange={(v) => setColFilter('reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                ) : null}
                {showCol('name') ? (
                  <TableColumnHeaderFilter columnKey="name" label={p('col_name')} value={colFilters.name ?? ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} />
                ) : null}
                {showCol('category') ? <TableColumnHeaderFilter columnKey="category" label={p('col_category')} value={colFilters.category ?? ''} onChange={(v) => setColFilter('category', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                {showCol('price') ? (
                  <TableColumnHeaderFilter columnKey="price" label={p('col_price')} value={colFilters.price ?? ''} onChange={(v) => setColFilter('price', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-right" />
                ) : null}
                {showCol('stock') ? (
                  <TableColumnHeaderFilter columnKey="stock" label={p('col_stock')} value={colFilters.stock ?? ''} onChange={(v) => setColFilter('stock', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" />
                ) : null}
                {showCol('location') ? <TableColumnHeaderFilter columnKey="location" label={p('col_location')} value={colFilters.location ?? ''} onChange={(v) => setColFilter('location', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} /> : null}
                {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-center" /> : null}
                <QawaemTableHeaderActionsCell label={t('common.actions')} columns={qawaemColumns} onToggle={handleToggleColumn} visibleCount={qawaemVisibleColumnCount} totalCount={qawaemColumns.length} thClassName={QAWAEM_TABLE_ACTIONS_TH_CLASS} />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 employees-table-body">
              {filteredParts.length > 0 ? (
                filteredParts.map((part, index) => (
                  <tr
                    key={part.id}
                    className={`hover:bg-white/5 transition-colors ${selectedParts.includes(part.id) ? 'bg-white/10' : ''}`}
                  >
                    <QawaemTableSelectRowCell checked={selectedParts.includes(part.id)} onChange={() => handleSelectPart(part.id)} dataLabel={p('label_select')} />
                    {showCol('reference') ? (
                      <td className="px-6 py-4 text-white font-mono text-sm" data-label={p('field_reference')}>{part.part_number}</td>
                    ) : null}
                    {showCol('name') ? (
                      <td className="px-6 py-4 text-white font-semibold" data-label={p('field_name')}>{part.name}</td>
                    ) : null}
                    {showCol('category') ? (
                      <td className="px-6 py-4 text-white/80" data-label={p('field_category')}>{getCategoryName(part.category)}</td>
                    ) : null}
                    {showCol('price') ? (
                      <td className="px-6 py-4 text-right text-white font-medium" data-label={p('field_price')}>{part.unit_price ? formatBrandMoney(part.unit_price) : p('na')}</td>
                    ) : null}
                    {showCol('stock') ? (
                      <td className="px-6 py-4 text-center" data-label={p('field_quantity')}>
                        <div className="flex flex-col items-center">
                          <span className="text-white font-bold">{part.quantity_in_stock}</span>
                          <span className="text-xs text-white/60">{p('min_stock_label', { value: part.min_stock_level })}</span>
                        </div>
                      </td>
                    ) : null}
                    {showCol('location') ? (
                      <td className="px-6 py-4 text-white/80" data-label={p('col_location')}>{part.location || p('na')}</td>
                    ) : null}
                    {showCol('status') ? (
                      <td className="px-6 py-4 text-center" data-label={p('field_status')}>
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={part.status} recordId={part.id} options={STATUS_OPTIONS} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })} />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onView={() => handleView(part)}
                        onEdit={() => handleEdit(part)}
                        onDelete={() => handleDelete(part.id)}
                        viewTitle={p('view_details')}
                        editTitle={p('btn_edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                    <Package size={48} className="mx-auto mb-4 text-white/30" />
                    <p className="text-lg">{p('empty_title')}</p>
                    <p className="text-sm mt-2">{p('empty_subtitle')}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingPart ? p('modal_edit_title') : p('modal_create_title')}
        icon={Package}
        formId="part-form"
        saveLabel={editingPart ? t('common.save') : t('common.create')}
        showSaveButton
        testId="parts-crud-modal"
      >
        <div className="-m-6 flex flex-col min-h-[320px]">
          <div className={`shrink-0 px-6 pt-3 border-b ${isLight ? 'border-gray-200' : 'border-white/10'}`}>
            <div className="flex flex-wrap gap-1" role="tablist" aria-label={editingPart ? p('modal_edit_title') : p('modal_create_title')}>
              {formTabs.map((tab) => {
                const TabIcon = tab.icon;
                const active = formTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setFormTab(tab.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${
                      active
                        ? (isLight
                          ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px'
                          : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px')
                        : (isLight
                          ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80'
                          : 'text-white/55 hover:text-white hover:bg-white/5')
                    }`}
                  >
                    <TabIcon size={16} className={active ? (tab.id === 'pricing' ? 'text-blue-400' : tab.id === 'stock' ? 'text-amber-400' : 'text-emerald-400') : ''} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="overflow-y-auto p-6 flex-1 min-h-0">
            <form id="part-form" onSubmit={handleSubmit}>
              {formTab === 'info' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_reference')}</label>
                    <input type="text" value={formData.part_number} onChange={(e) => setFormData({ ...formData, part_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('field_reference')} />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('field_name_required')} /></label>
                    <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('field_name_placeholder')} />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_description')}</label>
                    <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('field_description_placeholder')} />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_supplier')}</label>
                    <CustomSelect
                      value={formData.supplier}
                      onChange={(value) => setFormData({ ...formData, supplier: value })}
                      options={suppliers
                        .map((s) => {
                          const name = s.company_name || s.name || '';
                          return { value: name, label: name || p('no_name'), isDefault: false };
                        })
                        .filter((o) => o.value)}
                      placeholder={p('select_supplier_placeholder')}
                      searchable={true}
                      allowCreate={true}
                      createLabel={p('create_supplier_label')}
                      autoSelect={false}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_category')}</label>
                    <CustomSelect
                      value={formData.category}
                      onChange={(value) => setFormData({ ...formData, category: value })}
                      options={categorySelectOptions}
                      placeholder={p('field_category_placeholder')}
                      searchable
                      autoSelect={false}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_unit_price')}</label>
                    <CustomNumberInput
                      value={formData.unit_price}
                      onChange={(value) => setFormData({ ...formData, unit_price: value })}
                      placeholder="5000.00"
                      step={0.01}
                      min={0}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_status')}</label>
                    <CustomSelect
                      value={formData.status}
                      onChange={(value) => setFormData({ ...formData, status: value })}
                      options={[
                        { value: 'available', label: p('status_option_available'), isDefault: false },
                        { value: 'low_stock', label: p('status_option_low_stock'), isDefault: false },
                        { value: 'out_of_stock', label: p('status_option_out_of_stock'), isDefault: false }
                      ]}
                      placeholder={p('select_status_placeholder')}
                      autoSelect={false}
                    />
                  </div>
                </div>
              )}

              {formTab === 'stock' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_quantity')}</label>
                    <CustomNumberInput
                      value={formData.quantity_in_stock}
                      onChange={(value) => setFormData({ ...formData, quantity_in_stock: value })}
                      placeholder="100"
                      min={0}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('field_min_stock')}</label>
                    <CustomNumberInput
                      value={formData.min_stock_level}
                      onChange={(value) => setFormData({ ...formData, min_stock_level: value })}
                      placeholder="10"
                      min={0}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_location')}</label>
                    <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('field_location_placeholder')} />
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </QawaemErpCrudModal>

      {showViewModal && viewingPart ? (
      <QawaemErpViewModal
        open
        onClose={() => { setShowViewModal(false); setViewingPart(null); }}
        title={viewingPart?.name ?? p('view_title')}
        icon={Package}
        maxWidth="1100px"
        testId="parts-view-modal"
      >
        <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <Package className="h-5 w-5 text-emerald-400" />
                  <h3 className="text-white font-semibold">{p('section_info')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-sm">{p('field_reference')}</p><p className="text-white font-mono">{viewingPart.part_number}</p></div>
                  <div><p className="text-white/60 text-sm">{p('field_name')}</p><p className="text-white font-medium">{viewingPart.name}</p></div>
                  {viewingPart.description && (<div className="col-span-2"><p className="text-white/60 text-sm mb-1">{p('field_description')}</p><p className="text-white">{viewingPart.description}</p></div>)}
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="h-5 w-5 text-blue-400" />
                  <h3 className="text-white font-semibold">{p('section_pricing_category')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-sm">{p('field_category')}</p><p className="text-white">{getCategoryName(viewingPart.category)}</p></div>
                  <div><p className="text-white/60 text-sm">{p('field_price')}</p><p className="text-white font-bold text-xl">{viewingPart.unit_price ? formatBrandMoney(viewingPart.unit_price) : p('na')}</p></div>
                  <div><p className="text-white/60 text-sm mb-2">{p('field_status')}</p><div className="flex items-start">{getStatusBadge(viewingPart.status)}</div></div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <Layers className="h-5 w-5 text-amber-400" />
                  <h3 className="text-white font-semibold">{p('section_stock_location')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-sm">{p('field_quantity')}</p><p className="text-white font-bold text-xl">{viewingPart.quantity_in_stock}</p></div>
                  <div><p className="text-white/60 text-sm">{p('field_min_stock')}</p><p className="text-white">{viewingPart.min_stock_level}</p></div>
                  <div><p className="text-white/60 text-sm">{p('col_location')}</p><p className="text-white">{viewingPart.location || p('na')}</p></div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <Truck className="h-5 w-5 text-red-400" />
                  <h3 className="text-white font-semibold">{p('section_supplier')}</h3>
                </div>
                <div><p className="text-white/60 text-sm">{p('field_supplier_name')}</p><p className="text-white">{viewingPart.supplier || p('na')}</p></div>
              </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <button onClick={() => { setShowViewModal(false); setViewingPart(null); handleEdit(viewingPart); }} className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/30">{p('btn_edit')}</button>
              <button onClick={() => { setShowViewModal(false); setViewingPart(null); }} className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all duration-300">{p('btn_close')}</button>
            </div>
        </div>
      </QawaemErpViewModal>
      ) : null}
            <AppConfirmDialog
        open={deleteConfirm.open}
        title={t('common.delete', 'حذف')}
        message={
          deleteConfirm.ids.length > 1
            ? p('bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : p('delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteConfirmed}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default PartsPage;

